/**
 * vocalisationDetector.js - RMS envelope to onset/offset events
 *
 * The judgement half of Phase 1 B lives in the engine
 * (client/src/engine/judgement.js). This is the sensor half: a small state
 * machine that turns a stream of RMS frames into vocalisation events with an
 * onset, an offset and a duration.
 *
 * It is deliberately separate from useAudioEnergy.js and contains no Web Audio,
 * no DOM and no clock - every frame arrives with its own timestamp. That is
 * what makes the detection rules testable without a microphone, which matters
 * because "did the child make a sound" is the box Phase 1 turns on for
 * pre-verbal children and it must not be verified by hand.
 *
 * ---------------------------------------------------------------------------
 * WHY A NOISE FLOOR AND NOT A FIXED THRESHOLD
 *
 * The same absolute RMS is a shout on a headset and silence on a laptop
 * microphone three feet away. A fixed threshold measures the hardware, which
 * is the mistake COMPONENT-JUDGEMENT.md section 6.3 names for confidence and
 * which applies here with more force - confidence is at least normalised.
 *
 * So the detector calibrates against the room it is in, then triggers on a
 * multiple of that floor. `absoluteFloor` sits underneath as a guard: a
 * perfectly silent digital stream has a floor near zero, and any multiple of
 * zero is zero.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE 150 ms RULE IS APPLIED - NOT HERE
 *
 * Section 5 requires a vocalisation "sustained >= 150 ms". That threshold is
 * applied by the engine (trial.qualifyingVocalisations), not by this detector.
 * The detector reports what it saw and marks each event `sustained`; the
 * engine decides what counts. One threshold, one place, auditable from the
 * stored trial record after the fact.
 *
 * `minReportMs` below only suppresses obvious clicks and is an order of
 * magnitude under 150 ms, so it can never change a judgement.
 * ========================================================================= */

export const DETECTOR_DEFAULTS = Object.freeze({
  /** Frames collected before detection begins, to learn the room. */
  calibrationMs: 400,
  /** Onset when rms >= noiseFloor * onsetFactor (and >= absoluteFloor). */
  onsetFactor: 3.0,
  /** Offset when rms < noiseFloor * offsetFactor. Hysteresis: below onset. */
  offsetFactor: 1.8,
  /** Hard minimum onset threshold, so a near-silent stream cannot self-trigger. */
  absoluteFloor: 0.004,
  /** Quiet must persist this long before an event is closed. Rides out stops. */
  hangoverMs: 120,
  /** Events shorter than this are clicks, not vocalisations. Never >= 150. */
  minReportMs: 30,
  /** Force-close a runaway event (a fan, a sibling talking, a held note). */
  maxDurationMs: 10000,
  /** Marks an event as meeting the section 5 Phase 1 B duration rule. */
  sustainedMs: 150,
  /** EMA weight for tracking a drifting noise floor while idle. */
  floorAdapt: 0.02,
  /** Envelope samples retained. At 60 Hz this is ~50 seconds. */
  envelopeLimit: 3000,
  /** Assumed frame period until two frames have been seen. ~60 Hz. */
  framePeriodMs: 16.7,
});

/** Median, used for the floor so a cough during calibration does not set it. */
function median(values) {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Create a detector.
 *
 * @param {object} [options] overrides for DETECTOR_DEFAULTS
 * @returns {object} see the methods below
 */
export function createVocalisationDetector(options = {}) {
  const cfg = { ...DETECTOR_DEFAULTS, ...options };

  let state = 'calibrating';       // calibrating | idle | vocalising
  let noiseFloor = null;
  let calibrationSamples = [];
  let calibrationStartedAt = null;

  let current = null;              // the open event, if any
  let quietSince = null;           // start of the current below-offset run
  let envelope = [];
  let events = [];

  // A frame timestamp marks the START of the audio it summarises, so an event
  // that ends on the last above-threshold frame is one frame short. At 60 Hz
  // that is ~17 ms, and a genuine 150 ms vocalisation would measure 133 ms and
  // fail the section 5 rule. The period is tracked rather than assumed,
  // because the frame rate is whatever the caller's loop achieves.
  let lastFrameAt = null;
  let framePeriodMs = cfg.framePeriodMs;

  const onsetThreshold = () =>
    Math.max((noiseFloor ?? 0) * cfg.onsetFactor, cfg.absoluteFloor);

  const offsetThreshold = () =>
    Math.max((noiseFloor ?? 0) * cfg.offsetFactor, cfg.absoluteFloor * (cfg.offsetFactor / cfg.onsetFactor));

  function closeEvent(endedAt, cause) {
    const ev = current;
    current = null;
    quietSince = null;
    state = 'idle';
    if (!ev) return null;

    const durationMs = endedAt - ev.startedAt;
    if (durationMs < cfg.minReportMs) return null;

    const finished = {
      type: 'vocalisation',
      startedAt: ev.startedAt,
      endedAt,
      durationMs,
      peakRms: ev.peakRms,
      meanRms: ev.frames ? ev.sumRms / ev.frames : 0,
      noiseFloor,
      sustained: durationMs >= cfg.sustainedMs,
      cause,
    };
    events.push(finished);
    return finished;
  }

  return {
    /**
     * Feed one analysis frame.
     *
     * @param {number} rms   0..~1, root-mean-square of the frame
     * @param {number} timeMs monotonic milliseconds (performance.now())
     * @returns {{state:string, emitted:Array<object>, rms:number,
     *            noiseFloor:number|null, isVocalising:boolean}}
     */
    pushFrame(rms, timeMs) {
      const emitted = [];
      if (!Number.isFinite(rms) || !Number.isFinite(timeMs)) {
        return { state, emitted, rms: 0, noiseFloor, isVocalising: state === 'vocalising' };
      }

      if (lastFrameAt !== null) {
        const gap = timeMs - lastFrameAt;
        // Ignore a stalled tab's multi-second gap; it is not the frame rate.
        if (gap > 0 && gap < 250) framePeriodMs = framePeriodMs * 0.8 + gap * 0.2;
      }
      lastFrameAt = timeMs;

      envelope.push({ t: timeMs, rms });
      if (envelope.length > cfg.envelopeLimit) envelope = envelope.slice(-cfg.envelopeLimit);

      // ---- calibration --------------------------------------------------
      if (state === 'calibrating') {
        if (calibrationStartedAt === null) calibrationStartedAt = timeMs;
        calibrationSamples.push(rms);
        if (timeMs - calibrationStartedAt >= cfg.calibrationMs) {
          noiseFloor = median(calibrationSamples);
          calibrationSamples = [];
          state = 'idle';
        }
        return { state, emitted, rms, noiseFloor, isVocalising: false };
      }

      const onAt = onsetThreshold();
      const offAt = offsetThreshold();

      // ---- idle -----------------------------------------------------------
      if (state === 'idle') {
        if (rms >= onAt) {
          current = { startedAt: timeMs, peakRms: rms, sumRms: rms, frames: 1 };
          quietSince = null;
          state = 'vocalising';
          emitted.push({ type: 'onset', at: timeMs, rms, noiseFloor, threshold: onAt });
        } else {
          // Track a drifting room, but only while nothing is happening, and
          // only downward-biased by the small weight - a slow EMA cannot be
          // walked up by a vocalisation it never sees.
          noiseFloor = noiseFloor === null
            ? rms
            : noiseFloor * (1 - cfg.floorAdapt) + rms * cfg.floorAdapt;
        }
        return { state, emitted, rms, noiseFloor, isVocalising: state === 'vocalising' };
      }

      // ---- vocalising ------------------------------------------------------
      current.peakRms = Math.max(current.peakRms, rms);
      current.sumRms += rms;
      current.frames += 1;

      if (rms >= offAt) {
        quietSince = null;
        current.lastAboveAt = timeMs;
      } else if (quietSince === null) {
        quietSince = timeMs;
      }

      const lastAbove = current.lastAboveAt ?? current.startedAt;

      if (quietSince !== null && timeMs - quietSince >= cfg.hangoverMs) {
        // The event ended when the sound did, not when the hangover expired.
        const endedAt = lastAbove + framePeriodMs;
        const done = closeEvent(endedAt, 'offset');
        emitted.push({ type: 'offset', at: endedAt, rms, noiseFloor });
        if (done) emitted.push(done);
      } else if (timeMs - current.startedAt >= cfg.maxDurationMs) {
        const done = closeEvent(timeMs, 'max_duration');
        emitted.push({ type: 'offset', at: timeMs, rms, noiseFloor, truncated: true });
        if (done) emitted.push(done);
      }

      return { state, emitted, rms, noiseFloor, isVocalising: state === 'vocalising' };
    },

    /**
     * Close any open event at `timeMs`.
     *
     * Called when a trial ends. A child still vocalising at the boundary must
     * not have that vocalisation dropped - it is the whole Phase 1 B success.
     *
     * @param {number} timeMs
     * @returns {Array<object>} events emitted by the flush
     */
    flush(timeMs) {
      if (state !== 'vocalising' || !current) return [];
      const at = Math.min(timeMs, (current.lastAboveAt ?? timeMs) + framePeriodMs);
      const done = closeEvent(Math.max(at, current.startedAt), 'flush');
      return done ? [{ type: 'offset', at, noiseFloor, flushed: true }, done] : [];
    },

    /** Discard events and envelope. Keeps the learned noise floor. */
    clear() {
      events = [];
      envelope = [];
    },

    /** Full reset, including the noise floor. Use when the microphone changes. */
    reset() {
      state = 'calibrating';
      noiseFloor = null;
      calibrationSamples = [];
      calibrationStartedAt = null;
      current = null;
      quietSince = null;
      envelope = [];
      events = [];
      lastFrameAt = null;
      framePeriodMs = cfg.framePeriodMs;
    },

    /** Re-learn the room without discarding events. */
    recalibrate() {
      state = 'calibrating';
      calibrationSamples = [];
      calibrationStartedAt = null;
      current = null;
      quietSince = null;
    },

    /** Closed vocalisation events, oldest first. */
    getEvents() { return events.slice(); },

    /** The RMS envelope, oldest first. */
    getEnvelope() { return envelope.slice(); },

    /**
     * The `audio` block of a trial record (see engine/trial.js).
     * @param {object} [range] {from, to} to clip to a trial window
     */
    getAudioBlock(range = {}) {
      const from = Number.isFinite(range.from) ? range.from : -Infinity;
      const to = Number.isFinite(range.to) ? range.to : Infinity;
      return {
        noiseFloor,
        monitored: true,
        vocalisations: events
          .filter((e) => e.endedAt >= from && e.startedAt <= to)
          .map((e) => ({
            startedAt: e.startedAt,
            endedAt: e.endedAt,
            durationMs: e.durationMs,
            peakRms: e.peakRms,
            meanRms: e.meanRms,
          })),
        envelope: envelope.filter((s) => s.t >= from && s.t <= to),
      };
    },

    getState() {
      return {
        state,
        noiseFloor,
        onsetThreshold: onsetThreshold(),
        offsetThreshold: offsetThreshold(),
        isVocalising: state === 'vocalising',
        openedAt: current ? current.startedAt : null,
        framePeriodMs,
        eventCount: events.length,
      };
    },

    config: Object.freeze({ ...cfg }),
  };
}

/**
 * RMS of one time-domain frame.
 *
 * @param {Float32Array|number[]} samples -1..1
 * @returns {number}
 */
export function rmsOf(samples) {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return samples.length ? Math.sqrt(sum / samples.length) : 0;
}

/**
 * RMS of a byte time-domain frame, for browsers without
 * getFloatTimeDomainData (older Safari). 128 is silence.
 *
 * @param {Uint8Array} bytes
 * @returns {number}
 */
export function rmsOfBytes(bytes) {
  let sum = 0;
  for (let i = 0; i < bytes.length; i++) {
    const v = (bytes[i] - 128) / 128;
    sum += v * v;
  }
  return bytes.length ? Math.sqrt(sum / bytes.length) : 0;
}

export default { createVocalisationDetector, DETECTOR_DEFAULTS, rmsOf, rmsOfBytes };
